import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import SubServiceGrid from '../../components/seo/SubServiceGrid';
import { getGallery } from '../../seo/galleryConfig';

const plumbingSubServices = [
  { label: 'Emergency Plumber', href: '/services/emergency-plumber', blurb: '24/7 callouts for burst pipes, leaks and no-heat situations.' },
  { label: 'Leak Detection & Repairs', href: '/services/leak-detection-repairs', blurb: 'Acoustic + thermal imaging detection. Insurance-ready reports.' },
  { label: 'Burst Pipes', href: '/services/emergency-plumber', blurb: 'Same-call response. Containment, isolation, repair.' },
  { label: 'Tap Repairs & Replacements', href: '/services/tap-repairs', blurb: 'Dripping taps, stiff handles, new mixers fitted same day.' },
  { label: 'Toilet Repairs & Installations', href: '/services/toilet-repairs', blurb: 'Flush issues, cistern repairs, full pan replacement.' },
  { label: 'Blocked Drains', href: '/services/blocked-drains', blurb: 'Rodding, snake and high-pressure jetting for tough blockages.' },
  { label: 'Kitchen Plumbing', blurb: 'Sinks, dishwashers, washing-machine connections, waste re-routing.' },
  { label: 'Bathroom Plumbing', blurb: 'Basins, baths, showers, valves and full bathroom installs.' },
  { label: 'Radiator Repairs', href: '/services/radiator-repairs', blurb: 'Cold-radiator diagnosis, leaks, balancing, smart-TRV upgrades.' },
  { label: 'Radiator Replacements', href: '/services/radiator-repairs', blurb: 'Like-for-like swap or design-led modern replacement.' },
  { label: 'Pipework Repairs', blurb: 'Lead, copper, push-fit — section replacement and upgrades.' },
  { label: 'Outside Taps', href: '/services/tap-repairs', blurb: 'Garden tap install, frost protection, non-return-valve upgrades.' },
  { label: 'Water Pressure Issues', blurb: 'Low-pressure diagnosis, PRV installs, supply-pipe upgrades.' },
  { label: 'Full Re-Plumbs', blurb: 'Complete domestic re-plumb projects — older properties a speciality.' },
  { label: 'General Plumbing Maintenance', href: '/services/property-maintenance', blurb: 'Routine inspections and planned servicing for landlords + agents.' },
];

const faqs = [
  {
    q: 'How quickly can a plumber get to my property?',
    a: 'For standard plumbing enquiries we typically attend within 24 to 48 hours, and most jobs across Kent, Greater London and Essex can be slotted in the same week. For emergency callouts (burst pipes, major leaks, blocked toilets) we aim for under 90 minutes when our team has capacity in your area.'
  },
  {
    q: 'Do you charge a callout fee?',
    a: 'We are transparent about pricing — there is no hidden callout fee. You receive a clear quote either over the phone or after a brief on-site inspection, and the agreed price is fixed before any work begins. We do not start a job without your written or verbal agreement.'
  },
  {
    q: 'Are your plumbers insured and qualified?',
    a: 'Yes. Every H.A.R.P Solutions engineer holds the relevant trade qualifications, carries public liability insurance, and is vetted before joining the team. We are happy to share insurance certificates and credentials on request before any work commences.'
  },
  {
    q: 'Which areas do you cover for plumbing work?',
    a: 'We cover Greater London, the whole of Kent (Rochester, Gillingham, Chatham, Maidstone, Dartford, Gravesend, Sittingbourne and surrounding villages) and the major Essex towns including Romford, Basildon, Chelmsford, Brentwood and Southend. If you are unsure whether we cover your postcode, give us a quick call.'
  },
  {
    q: 'Can you handle both domestic and commercial plumbing?',
    a: 'Absolutely. We work with private homeowners, landlords, lettings agents, café and restaurant owners, gyms, offices, and small commercial premises. Our engineers carry both domestic and light-commercial certifications.'
  },
  {
    q: 'Do you provide written quotes and invoices?',
    a: 'Yes — every customer receives a written invoice with VAT details, a description of the work completed, and a parts breakdown. We strongly recommend this for tenants and landlords as it forms part of your maintenance records.'
  },
];

const relatedServices = [
  { slug: 'emergency-plumber', label: 'Emergency Plumber', blurb: '24/7 callouts for burst pipes, leaks, and no-heat situations.' },
  { slug: 'handyman', label: 'Handyman Services', blurb: 'Property repairs, fittings, assembly, and minor carpentry.' },
  { slug: 'property-maintenance', label: 'Property Maintenance', blurb: 'Planned maintenance for landlords, agents and homeowners.' },
];

const PlumbingPage = () => (
  <ServicePageLayout
    seoTitle="Plumbing Services in Kent, London & Essex | Local Plumber | H.A.R.P Solutions LTD"
    seoDescription="Reliable local plumber covering Kent, London and Essex. Leak repairs, taps, toilets, blocked drains, kitchen and bathroom plumbing. Fully insured, transparent pricing. Call +44 330 122 1094."
    canonicalPath="/services/plumbing"
    keywords="plumbing services, local plumber, plumber near me, plumbing repairs, leak repair, plumber Kent, plumber London, plumber Essex"
    pageTitle="Trusted Local Plumber for Kent, London & Essex"
    serviceName="Plumbing Services"
    serviceSlug="plumbing"
    galleryImages={getGallery('plumbing')}
    heroSubheading="Honest pricing. Skilled engineers. Work that lasts."
    intro="From a dripping kitchen tap to a complete bathroom installation, H.A.R.P Solutions provides reliable plumbing services for homeowners, landlords and small businesses across Greater London, Kent and Essex. Our engineers turn up on time, explain the work clearly, and leave your property cleaner than they found it."
    bullets={[
      'Same-week appointments across most postcodes',
      'Transparent fixed-price quoting',
      'Fully insured engineers, every job documented',
      'Domestic and light-commercial coverage',
      'Free professional advice over the phone',
      'No job too small — tap washer to full re-plumb',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
  >
    <h2>Plumbing Services Designed Around You</h2>
    <p>
      A leaking pipe under the sink at 8am on a Monday is the last thing anyone needs. Whether you are a busy homeowner, a landlord managing
      multiple properties, or a café owner who simply needs the toilets working before lunch service, you need a plumber who picks up the phone,
      arrives when promised, and gets the job done first time. That is exactly what we are built for.
    </p>
    <p>
      H.A.R.P Solutions LTD is a fully insured local plumbing company serving Kent, Greater London and Essex. Our engineers have decades of
      combined experience across domestic and light-commercial plumbing — from emergency leak repairs and blocked drains to complete bathroom
      installations and pipework upgrades. We pride ourselves on being the kind of plumber you can recommend without hesitation: clean, polite,
      thorough, and fairly priced.
    </p>

    <h2>Services We Cover</h2>
    <p>Our plumbing team handles every part of a typical UK property's water and waste system. Pick a sub-service to see more, or call us for help across any of them:</p>

    <SubServiceGrid
      title="Plumbing Sub-Services"
      subtitle="Click any tile with an arrow for the full dedicated page. Items without arrows are covered by our wider plumbing services — call us for a quote."
      items={plumbingSubServices}
    />

    <h2 className="!mt-12">Why Customers Choose H.A.R.P Solutions</h2>
    <p>
      There is no shortage of plumbers in Kent, London and Essex — so we work hard to be different in a way that matters. Three principles
      guide everything we do:
    </p>
    <h3>1. Honest, fixed-price quotes</h3>
    <p>
      We will never start a job without first agreeing the price with you. If we discover a complication during the work, we stop, call you,
      and explain the options before adding anything to the bill. No surprises. No "we found another fault we had to fix" charges.
    </p>
    <h3>2. Engineers, not salespeople</h3>
    <p>
      You speak to people who have actually held a spanner. When you call <a href="tel:+443301221094">+44 330 122 1094</a>, you'll get clear
      advice on whether the job is genuinely urgent, what is likely to be involved, and a realistic estimate before we ever come out.
    </p>
    <h3>3. Properly insured, properly accountable</h3>
    <p>
      Every engineer is covered by full public liability insurance, every job is documented with photos before and after, and every customer
      receives a written invoice. If a problem returns within our workmanship guarantee, we come back and put it right at no extra cost.
    </p>

    <h2>Areas We Cover</h2>
    <p>
      Our engineers are spread across the region so we can usually reach you the same week — and within hours for emergency callouts. Core
      coverage includes:
    </p>
    <ul>
      <li><strong>Kent:</strong> Rochester, Gillingham, Chatham, Maidstone, Dartford, Gravesend, Sittingbourne, Tonbridge and surrounding villages.</li>
      <li><strong>Greater London:</strong> all 32 boroughs, with concentrated coverage in East London, South-East London and the M25 corridor.</li>
      <li><strong>Essex:</strong> Romford, Basildon, Brentwood, Chelmsford, Southend-on-Sea, and surrounding commuter towns.</li>
    </ul>
    <p>
      If your postcode sits outside these areas, we still want to hear from you — for larger jobs we are happy to travel further across the
      South-East. Give us a call to confirm.
    </p>

    <h2>What Happens After You Contact Us?</h2>
    <ol>
      <li><strong>Initial call or message.</strong> Describe the problem in your own words — we'll ask the right questions to scope it accurately.</li>
      <li><strong>Quote.</strong> For straightforward jobs we'll give you a fixed quote on the spot. For complex work we may arrange a 15-minute site visit, often free.</li>
      <li><strong>Booking.</strong> We agree a date, send you a confirmation, and put it in your calendar. No "between 8 and 5" windows — we give you a tight arrival slot.</li>
      <li><strong>The job.</strong> Engineer arrives, talks you through the work, completes it cleanly, tests everything, and tidies up.</li>
      <li><strong>Aftercare.</strong> Written invoice, photos, and a workmanship guarantee. We are easy to reach if anything needs revisiting.</li>
    </ol>

    <h2>Ready to Book?</h2>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> for immediate help, or scroll down and use the quote form — we
      reply quickly during working hours and within a few hours overnight. For a 24/7 emergency, see our{' '}
      <a href="/services/emergency-plumber">emergency plumber page</a>.
    </p>
  </ServicePageLayout>
);

export default PlumbingPage;
